import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  Menu,
  MenuItem,
  ArrowDropDownIcon,
} from '@cherre-frontend/ui';
import ConnectorDetailsHeader from './components/Header';
import {
  useCherreState,
  useCherreSetState,
  useCherreValue,
} from '@cherre-frontend/data-fetching';
import {
  addConnectorSelectedNavLink,
  connectorDataValidationTabParam,
  connectorDetailsTabParam,
  connectorDetailsTabState,
} from '../../recoil';
import {
  ConnectorDetailTabs,
  ConnectorDetailTab,
  ConnectorTitle,
  SelectTabsContainer,
  DropdownActionTitleContainer,
} from './styles';
import TableProfileContent from './components/TableProfileContent/TableProfileContent';
import ColumnProfileContent from './components/ColumnProfile/ColumnProfileContent';
import LayoutWithoutSideNav from '../../components/LayoutWithoutSideNav';
import DataValidationContent from './components/DataValidation/DataValidationContent';
import {
  connectorDetailsDataValidationState,
  dataValidationRuleForm,
} from './recoil';
import { DataValidationState } from './components/DataValidation/utils/types';
import CreateValidationRuleForm from './components/DataValidation/CreateValidationRuleForm';
import TableSelectionForm from './components/DataValidation/TableSelectionForm';
import DataDictionaryContent from './components/DataDictionary/DataDictionaryContent';
import OverviewContent from './components/Overview/OverviewContent';
import DataModelContent from './components/DataModel/DataModelContent';
import { emptyForm } from './components/DataValidation/utils/const';
import ValidationDetailsPage from '../validation-details';
import { useHistory, useLocation } from 'react-router';
import {
  ConnectorDetailsRoute,
  ValidationTestDetailsRoute,
} from '../../routes';
import { ConnectorsPageContainer } from '../../components/ConnectorsPageContainer';
import { DataValidationV2Content } from './components/DataValidationV2';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import { DataValidationV2TabContent } from './components/DataValidationV2/hooks/useDataValidationV2SearchParams';
import { useGetOverview } from './components/Overview/hooks/useGetOverview';
import ConnectorErrorBanner from './components/Overview/ConnectorErrorBanner';
import ConnectorStageHeader from 'src/products/connector/pages/connector-details/components/ConnectorStageHeader';
import EnviromnentsConnectorsSelect from '../../components/EnviromnentsSelect';
import { insertStageParamUrl } from './components/Overview/util/insertStageParamUrl';
import { getConfigsParamFromUrl } from '../../utils/getConfigsParamFromUrl';
import { ConnectorStages } from '../../constants';
// Will be released soon
// import DropdownActionsConnectorOverview from './components/DropdownActionsConnectorOverview';
// import ConnectorRevisionsModal from './components/ConnectorRevisionsModal';
import { useConnectorOverviewUI } from './contexts/ConnectorOverviewUI';

export enum ConnectorDetailsTabValues {
  OVERVIEW = 'Overview',
  OBSERVABILITY = 'Observability',
  VALIDATION = 'Data Validation',
  DATA_MODEL = 'Data Model',
  DICTIONARY = 'Dictionary',
  API_METRICS = 'API Metrics',
}

const ConnectorDetailsPage: React.FC = () => {
  const path = useLocation().pathname;
  const history = useHistory();
  const route = useCherreValue(
    path.includes('/validation-details')
      ? ValidationTestDetailsRoute.routeParamSelector
      : ConnectorDetailsRoute.routeParamSelector
  );

  const [activeNavLink, setActiveNavLink] = useCherreState(
    addConnectorSelectedNavLink
  );
  const [dataValidationState] = useCherreState(
    connectorDetailsDataValidationState
  );
  const [connectorOverviewUI, setConnectorOverviewUI] =
    useConnectorOverviewUI();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [tab, setTab] = useCherreState(connectorDetailsTabState);
  const stage =
    (connectorOverviewUI.data?.stage as ConnectorStages) ||
    ConnectorStages.PUBLISH;
  const { connector, stagesStatuses } = useGetOverview(stage);
  const canAddConnector = useFeatureFlag('ConnectorsCreateConnector');

  useEffect(() => {
    const { stage, tab } = getConfigsParamFromUrl();
    setConnectorOverviewUI({ data: { stage } });

    if (tab) {
      setTimeout(() => {
        setTab(tab);
      }, 50);
    }
  }, []);

  useEffect(() => {
    insertStageParamUrl(stage);
  }, [stage]);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const setForm = useCherreSetState(dataValidationRuleForm);

  const [tabParam, setTabParam] = useCherreState(connectorDetailsTabParam);
  const [dataValidationV2Param, setDataValidationV2Param] = useCherreState(
    connectorDataValidationTabParam
  );

  const isDataValidationV2 = useFeatureFlag('ConnectorsDataValidationV2');

  useEffect(() => {
    const hasValidationDetailsPath = path.includes('/validationDetails');
    const isValidationTab = tab === ConnectorDetailsTabValues.VALIDATION;

    if (hasValidationDetailsPath && !isValidationTab) {
      setTab(ConnectorDetailsTabValues.VALIDATION);
      return;
    }

    if (isDataValidationV2 && dataValidationV2Param?.content) {
      setTab(ConnectorDetailsTabValues.VALIDATION);
      setDataValidationV2Param(dataValidationV2Param);
      return;
    }

    if (!tabParam || !tab || tabParam === tab) {
      return;
    }

    setTab(tab);
  }, [path, tabParam, tab, setTab]);

  const dataValidationContent = useMemo(() => {
    if (isDataValidationV2) {
      return <DataValidationV2Content />;
    }

    switch (dataValidationState) {
      case DataValidationState.DASHBOARD:
        return <DataValidationContent />;
      case DataValidationState.DETAILS:
        return <ValidationDetailsPage />;
      case DataValidationState.TABLE_SELECTION:
        return <TableSelectionForm />;
      case DataValidationState.TABLE:
      case DataValidationState.COLUMN:
        return <CreateValidationRuleForm />;
      default:
        return <DataValidationContent />;
    }
  }, [dataValidationState]);

  // Will be released soon
  // const handleActionDropdown = useCallback((option) => {
  //   if (option === 'publish_revision') {
  //     setOpenModal(true);
  //   }
  // }, []);

  // const handleCloseRevisionModal = useCallback(() => {
  //   setOpenModal(false);
  // }, []);

  const handleStageSelectOnChange = useCallback((stage: string) => {
    setConnectorOverviewUI({ data: { stage } });
  }, []);

  return (
    <ConnectorsPageContainer>
      <ConnectorStageHeader stage={stage} />
      <Box sx={{ height: '100%', width: '100%' }}>
        <ConnectorDetailsHeader connector={connector} />

        <Grid container direction='column'>
          <SelectTabsContainer>
            <ConnectorDetailTabs
              indicatorColor='primary'
              textColor='primary'
              variant='standard'
              value={tab}
              onChange={(e, newValue) => {
                const isObservabilityTab =
                  newValue === ConnectorDetailsTabValues.OBSERVABILITY;

                const isDataValidationTab =
                  newValue === ConnectorDetailsTabValues.VALIDATION;

                setForm(emptyForm);

                if (path.includes('/validationDetails')) {
                  history.push(`/connectors/${route?.connector_name}`);
                }

                if (isDataValidationTab) {
                  setTab(ConnectorDetailsTabValues.VALIDATION);
                  setTabParam(ConnectorDetailsTabValues.VALIDATION);
                  setDataValidationV2Param({
                    content: DataValidationV2TabContent.LIST,
                    rule: null,
                    detailsTab: null,
                  });
                  return;
                } else {
                  setDataValidationV2Param(null);
                }

                if (!isObservabilityTab) {
                  setTab(newValue);
                  setTabParam(newValue);
                }
              }}
            >
              <ConnectorDetailTab
                value={ConnectorDetailsTabValues.OVERVIEW}
                label={ConnectorDetailsTabValues.OVERVIEW}
                style={{ minWidth: 'unset' }}
              />
              <ConnectorDetailTab
                value={ConnectorDetailsTabValues.OBSERVABILITY}
                label={
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      style={{ fontSize: '14px', fontWeight: 'bold' }}
                    >
                      {ConnectorDetailsTabValues.OBSERVABILITY}
                    </Typography>
                    <ArrowDropDownIcon />
                  </Box>
                }
                style={{ minWidth: 'unset' }}
                onClick={handleOpen}
              />
              <ConnectorDetailTab
                value={ConnectorDetailsTabValues.VALIDATION}
                label={ConnectorDetailsTabValues.VALIDATION}
                style={{ minWidth: 'unset' }}
              />
              <ConnectorDetailTab
                value={ConnectorDetailsTabValues.DATA_MODEL}
                label={ConnectorDetailsTabValues.DATA_MODEL}
                style={{ minWidth: 'unset' }}
              />
              <ConnectorDetailTab
                value={ConnectorDetailsTabValues.DICTIONARY}
                label={ConnectorDetailsTabValues.DICTIONARY}
                style={{ minWidth: 'unset' }}
              />
            </ConnectorDetailTabs>
            {canAddConnector && tab === ConnectorDetailsTabValues.OVERVIEW && (
              <EnviromnentsConnectorsSelect
                value={stage}
                onChange={handleStageSelectOnChange}
                stagesStatuses={stagesStatuses}
              />
            )}
          </SelectTabsContainer>
        </Grid>
        <Menu
          id='observability-menu'
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': 'basic-button',
          }}
          slotProps={{
            paper: {
              style: {
                width: anchorEl ? anchorEl.offsetWidth || 134 : 'unset',
                borderRadius: '10px 10px',
              },
            },
          }}
        >
          <MenuItem
            style={{ paddingLeft: '12px' }}
            onClick={() => {
              setTab(ConnectorDetailsTabValues.OBSERVABILITY);
              setTabParam(ConnectorDetailsTabValues.OBSERVABILITY);
              setActiveNavLink('Table Profile');
              handleClose();
            }}
          >
            Table Profile
          </MenuItem>

          <MenuItem
            style={{ paddingLeft: '12px' }}
            onClick={() => {
              setTab(ConnectorDetailsTabValues.OBSERVABILITY);
              setTabParam(ConnectorDetailsTabValues.OBSERVABILITY);
              setActiveNavLink('Column Profile');
              handleClose();
            }}
          >
            Column Profile
          </MenuItem>
        </Menu>

        {/* Will be released soon  */}
        {/* {openModal && (
            <ConnectorRevisionsModal
              isOpen={openModal}
              onClose={handleCloseRevisionModal}
              onSave={console.log}
              newRevisionVersion='003'
              connector={connector}
            />
          )} */}
        <DropdownActionTitleContainer>
          <ConnectorTitle>{connector.displayName}</ConnectorTitle>
          {/* Will be released soon  */}
          {/* <DropdownActionsConnectorOverview onChange={handleActionDropdown} /> */}
        </DropdownActionTitleContainer>
        {connector.failedComponentName &&
          tab === ConnectorDetailsTabValues.OVERVIEW && (
            <ConnectorErrorBanner
              connectorComponentName={connector.failedComponentName}
              statusMessage={connector.statusMessage}
            />
          )}
        {tab === ConnectorDetailsTabValues.OVERVIEW && (
          <LayoutWithoutSideNav>
            <OverviewContent connector={connector} stage={stage} />
          </LayoutWithoutSideNav>
        )}
        {tab === ConnectorDetailsTabValues.OBSERVABILITY &&
          activeNavLink === 'Table Profile' && (
            <LayoutWithoutSideNav>
              <TableProfileContent />
            </LayoutWithoutSideNav>
          )}
        {tab === ConnectorDetailsTabValues.OBSERVABILITY &&
          activeNavLink === 'Column Profile' && (
            <LayoutWithoutSideNav>
              <ColumnProfileContent />
            </LayoutWithoutSideNav>
          )}
        {tab === ConnectorDetailsTabValues.VALIDATION && (
          <LayoutWithoutSideNav>{dataValidationContent}</LayoutWithoutSideNav>
        )}
        {tab === ConnectorDetailsTabValues.DATA_MODEL && (
          <LayoutWithoutSideNav>
            <DataModelContent />
          </LayoutWithoutSideNav>
        )}
        {tab === ConnectorDetailsTabValues.DICTIONARY && (
          <LayoutWithoutSideNav>
            <DataDictionaryContent />
          </LayoutWithoutSideNav>
        )}
      </Box>
    </ConnectorsPageContainer>
  );
};

export default ConnectorDetailsPage;
